import { useEffect, useState, useRef, Suspense, lazy } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import Layout from '@/components/layout/Layout'
import AuthGuard from '@/components/auth/AuthGuard'
import GuestGuard from '@/components/auth/GuestGuard'
import { MantineProvider } from '@mantine/core'
import { theme } from './theme'
import { useAppSelector } from './store'
import { usePostHogIdentification } from './hooks/usePostHogIdentification'
import { Spinner } from '@shopify/polaris'
import { setStoreUrl, setAuthToken } from '@/utils/auth'
import { useLocation } from 'react-router-dom'
import OnboardingLayout from './components/layout/OnboardingLayout'
import { useQuery } from '@tanstack/react-query'
import storeApi from './api/storeApi'
import { BillingProvider } from './contexts/BillingContext'
import { setLiveSurveyCount } from './store/slices/surveySlice'
import { PlanUpgradeSuccessModal } from '@/components/PlanUpgradeSuccessModal'
import CreateSurveyModal from './features/surveys/components/CreateSurveyModal'
import MobileOverlay from '@/components/common/MobileOverlay'
import { isMobileDevice } from '@/utils/utils'
import { AUTH_STATUS } from '@/features/auth/constants'
import { useAppDispatch } from './store'
import {
    initializeAuth,
    selectIsSuperAdmin,
    selectIsImpersonating,
    setNeedsPasswordSetup,
    // ─── [RELEASE 1: Context Switching — DONE] ──────────────────
    // selectAvailableStores,
    // selectStoreId,
    // selectMerchantId,
    // switchContext,
    // ─── [/RELEASE 1] ───────────────────────────────────────────
} from '@/store/slices/authSlice'
import { PERMISSIONS } from '@/features/auth/constants'
import PermissionGuard from '@/components/auth/PermissionGuard'

// ─── Lazy-loaded: Auth Pages ──────────────────────────────────────
const LoginPage = lazy(() => import('@/features/auth/pages/LoginPage'))
const RegisterPage = lazy(() => import('@/features/auth/pages/RegisterPage'))
const ForgotPasswordPage = lazy(
    () => import('@/features/auth/pages/ForgotPasswordPage')
)
const ResetPasswordPage = lazy(
    () => import('@/features/auth/pages/ResetPasswordPage')
)
const AcceptInvitePage = lazy(
    () => import('@/features/auth/pages/AcceptInvitePage')
)
const SetupPasswordPage = lazy(
    () => import('@/features/auth/pages/SetupPasswordPage')
)
// ─── [OUT OF SCOPE: Magic Link Auth] ─────────────────────────────
// const MagicLinkVerifyPage = lazy(
//     () => import('@/features/auth/pages/MagicLinkVerifyPage')
// )
// ─── [/OUT OF SCOPE] ─────────────────────────────────────────────
// ─── [RELEASE 3: Google OAuth] ───────────────────────────────────
// const GoogleOAuthCallbackPage = lazy(
//     () => import('@/features/auth/pages/GoogleOAuthCallbackPage')
// )
// ─── [/RELEASE 3] ────────────────────────────────────────────────
const CheckYourEmailPage = lazy(
    () => import('@/features/auth/pages/CheckYourEmailPage')
)
const VerifyEmailPage = lazy(
    () => import('@/features/auth/pages/VerifyEmailPage')
)
const CompleteAccountPage = lazy(
    () => import('@/features/auth/pages/CompleteAccountPage')
)

// ─── Lazy-loaded: Legacy Auth (Shopify store picker) ──────────────
const LegacyLogin = lazy(() => import('@/features/auth/Login'))

// ─── [RELEASE 3: Super Admin + Device Management] ───────────────
const SuperAdminGuard = lazy(() => import('@/components/auth/SuperAdminGuard'))
const AdminLayout = lazy(() => import('@/features/admin/components/AdminLayout'))
const MerchantsPage = lazy(() => import('@/features/admin/pages/MerchantsPage'))
const AuditLogPage = lazy(() => import('@/features/admin/pages/AuditLogPage'))
const BillingDealsPage = lazy(() => import('@/features/admin/pages/BillingDealsPage'))
const PlansPage = lazy(() => import('@/features/admin/pages/PlansPage'))
const AdminUsersPage = lazy(() => import('@/features/admin/pages/AdminUsersPage'))
const DeviceManagementPage = lazy(() => import('@/features/admin/pages/DeviceManagementPage'))
const ImpersonationBanner = lazy(() => import('@/features/admin/components/ImpersonationBanner'))
// ─── [/RELEASE 3] ────────────────────────────────────────────────
const StoreRequiredGuard = lazy(() => import('@/components/auth/StoreRequiredGuard'))

// ─── Lazy-loaded: App Pages ──────────────────────────────────────
const Onboarding = lazy(() => import('./features/onboarding/pages/Onboarding'))
const Surveys = lazy(() => import('@/features/surveys/Surveys'))
const WhatsAppSettings = lazy(() => import('./features/whatsapp/WhatsAppSettings'))
const ConversationMonitor = lazy(() => import('./features/whatsapp/ConversationMonitor'))
const HelpCenter = lazy(() => import('./features/help/HelpCenter'))
const MeasureNetPromoterScore = lazy(
    () => import('./features/templates/MeasureNetPromoterScore')
)
const Analytics = lazy(() => import('./features/analytics/Analytics'))
const NpsByCustomer = lazy(() => import('./features/analytics/customer-nps-segment/CustomerNpsSegmentTab'))

const SurveyTemplates = lazy(() => import('./features/surveys/SurveyTemplates'))
const TemplateEditor = lazy(() => import('./features/templates/TemplateEditor'))
const SurveyBuilder = lazy(() => import('@/features/survey-builder/Layout'))
const Create = lazy(() => import('./features/survey-builder/Create'))
const CreateTemplate = lazy(
    () => import('./features/survey-builder/CreateTemplate')
)
const Template = lazy(() => import('./features/survey-builder/Template'))
const Billing = lazy(() => import('./features/onboarding/pages/BIlling'))
const BookACall = lazy(() => import('./features/onboarding/pages/BookACall'))
const AISurveyGenerator = lazy(
    () => import('./features/ai-survey/AISurveyGenerator')
)
const IntegrationsPage = lazy(
    () => import('./features/settings/pages/IntegrationsPage')
)
const ProfilePage = lazy(
    () => import('./features/settings/pages/ProfilePage')
)
const BillingPage = lazy(
    () => import('./features/settings/pages/BillingPage')
)
const TeamPage = lazy(
    () => import('./features/settings/pages/TeamPage')
)
const McpPage = lazy(
    () => import('./features/settings/pages/McpPage')
)
const AgenticLayoutPage = lazy(
    () => import('@/features/agent/components/AgenticLayout')
)

// Route-level wrapper for Agentic Mode. Mounted outside the main <Layout/> so the
// console takes over the full viewport; the "Exit Agentic Mode" pill in the workspace
// sidebar pops back to "/". The fixed-inset wrapper is required because AgenticLayout
// uses h-full and has no sized ancestor at the route level.
const AgenticRoute = () => {
    const navigate = useNavigate()
    return (
        <div className='fixed inset-0 bg-white'>
            <AgenticLayoutPage
                agenticMode={true}
                onAgenticToggle={(next) => {
                    if (!next) navigate('/')
                }}
            />
        </div>
    )
}

// ── Loading component ──
const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-screen">
        <Spinner />
    </div>
)

// ─── URL Parameter Handler ────────────────────────────────────────
// Handles legacy ?token= from Shopify redirects + ?charge_id= billing
const useUrlParameters = () => {
    const location = useLocation()
    const dispatch = useAppDispatch()
    const [showUpgradeSuccess, setShowUpgradeSuccess] = useState(false)

    useEffect(() => {
        const params = new URLSearchParams(location.search)

        // Handle Shopify redirect tokens in URL
        if (params.has('token')) {
            const token = params.get('token')!
            const storeUrl = params.get('storeUrl')

            // Save token to cookies so apiClient interceptor can attach it
            setAuthToken(token)
            if (storeUrl) setStoreUrl(storeUrl)

            // Clean the URL after token is saved
            window.history.replaceState({}, '', window.location.pathname)

            // Re-initialize auth with the saved token
            dispatch(initializeAuth())
            return
        }

        // Handle billing upgrade success
        if (
            params.has('charge_id') &&
            localStorage.getItem('selectedPlanUpgrade') === 'true'
        ) {
            setTimeout(() => {
                setShowUpgradeSuccess(true)
            }, 5000)
            localStorage.removeItem('selectedPlanUpgrade')
            window.history.replaceState({}, '', '/')
        }
    }, [location.search, dispatch])

    return { showUpgradeSuccess, setShowUpgradeSuccess }
}

// ─── Store Details Hook ───────────────────────────────────────────
// Fetches store details when user is authenticated
const useStoreDetails = (isAuthenticated: boolean) => {
    const dispatch = useAppDispatch()
    const isSuperAdmin = useAppSelector(selectIsSuperAdmin)
    const isImpersonating = useAppSelector(selectIsImpersonating)
    const { merchantId, storeId } = useAppSelector((state) => state.auth.context)

    // Only fetch if authenticated AND acting as a merchant (not a clean super admin)
    const shouldFetch = isAuthenticated && (!isSuperAdmin || isImpersonating)

    const getStoreDetails = async () => {
        try {
            const response = await storeApi.getStoreDetails()
            const { store, metadata } = response

            if (typeof metadata === 'string') {
                try {
                    JSON.parse(metadata)
                } catch (parseError) {
                    console.error('Failed to parse metadata:', parseError)
                }
            }

            dispatch(setLiveSurveyCount(store.totalPublishedSurveys || 0))
            dispatch(setNeedsPasswordSetup(response.meta?.needsPasswordSetup === true))
            return response
        } catch (error) {
            console.error('Failed to fetch store details:', error)
            throw error
        }
    }

    const {
        data: storeData,
        isLoading: storeLoading,
        isInitialLoading,
    } = useQuery({
        queryKey: ['storeDetails', merchantId || '', storeId || ''],
        queryFn: getStoreDetails,
        enabled: shouldFetch,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        staleTime: 5 * 60 * 1000,
        retry: 2,
    })

    return {
        storeData,
        storeLoading,
        isInitialStoreLoading: isInitialLoading,
    }
}

// ─── Mobile Detection ─────────────────────────────────────────────
const useMobileDetection = () => {
    const [isMobile, setIsMobile] = useState(false)

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(isMobileDevice())
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])

    return isMobile
}

const AppContent = () => {
    const authStatus = useAppSelector((state) => state.auth.status)
    const isSuperAdmin = useAppSelector(selectIsSuperAdmin)
    const isImpersonating = useAppSelector(selectIsImpersonating)
    const dispatch = useAppDispatch()
    const isAuthenticated = authStatus === AUTH_STATUS.AUTHENTICATED || authStatus === AUTH_STATUS.MIGRATING
    const { storeData } = useStoreDetails(isAuthenticated)
    const { showUpgradeSuccess, setShowUpgradeSuccess } = useUrlParameters()
    const isMobile = useMobileDetection()

    // ─── Continuous revalidate of /auth/me (every 5s) ─────────────────
    // Purpose: reflect admin-side changes without manual refresh.
    // Efficiency: only runs while tab is visible, and only for merchant sessions.
    const pollTimerRef = useRef<number | null>(null)

    useEffect(() => {
        const isAdminOnlySession = isSuperAdmin && !isImpersonating
        if (!isAuthenticated || isAdminOnlySession) return

        const start = () => {
            if (pollTimerRef.current !== null) return
            dispatch(initializeAuth())
            pollTimerRef.current = window.setInterval(() => {
                dispatch(initializeAuth())
            }, 5000)
        }

        const stop = () => {
            if (pollTimerRef.current === null) return
            window.clearInterval(pollTimerRef.current)
            pollTimerRef.current = null
        }

        const onVisibility = () => {
            if (document.visibilityState === 'visible') start()
            else stop()
        }

        document.addEventListener('visibilitychange', onVisibility)
        // Start immediately if visible
        if (document.visibilityState === 'visible') start()

        return () => {
            document.removeEventListener('visibilitychange', onVisibility)
            stop()
        }
    }, [dispatch, isAuthenticated, isSuperAdmin, isImpersonating])

    // ─── [RELEASE 1: Context Switching — Auto-switch to Shopify store — DONE] ──
    // const currentStoreId = useAppSelector(selectStoreId)
    // const currentMerchantId = useAppSelector(selectMerchantId)
    // const availableStores = useAppSelector(selectAvailableStores)
    // const isSuperAdmin = useAppSelector(selectIsSuperAdmin)
    // const isSwitchingRef = useRef(false)
    //
    // useEffect(() => {
    //     if (!isAuthenticated || isSuperAdmin || isSwitchingRef.current) return
    //     if (!availableStores || availableStores.length === 0) return
    //
    //     // Check if the current store is the default (custom) store
    //     const currentStore = availableStores.find((s) => s.storeId === currentStoreId)
    //     if (!currentStore || currentStore.platform !== 'custom') return
    //
    //     // Find a Shopify store to switch to (prefer same merchant)
    //     const shopifyStore = availableStores.find(
    //         (s) => s.platform === 'shopify' && s.merchantId === currentStore.merchantId
    //     ) || availableStores.find((s) => s.platform === 'shopify')
    //
    //     if (!shopifyStore) return
    //
    //     // Auto-switch context to the Shopify store
    //     isSwitchingRef.current = true
    //     dispatch(switchContext({
    //         storeId: shopifyStore.storeId,
    //         merchantId: shopifyStore.merchantId || currentMerchantId || undefined,
    //     })).unwrap().then(() => {
    //         window.location.href = '/'
    //     }).catch(() => {
    //         isSwitchingRef.current = false
    //     })
    // }, [isAuthenticated, currentStoreId, currentMerchantId, availableStores, isSuperAdmin, dispatch])
    // ─── [/RELEASE 1] ────────────────────────────────────────────────

    // Identify user with PostHog
    usePostHogIdentification()

    // Show mobile overlay
    if (isMobile) {
        return <MobileOverlay />
    }

    return (
        <BillingProvider>
            <MantineProvider theme={theme}>
                <Suspense fallback={<LoadingSpinner />}>
                    {/* ─── [RELEASE 3: Super Admin Impersonation] ─── */}
                    <ImpersonationBanner />
                    {/* ─── [/RELEASE 3] ─── */}
                    <Routes>
                        {/* ═══ Public Auth Routes (GuestGuard) ═══ */}
                        <Route element={<GuestGuard />}>
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/register" element={<RegisterPage />} />
                            <Route
                                path="/check-your-email"
                                element={<CheckYourEmailPage />}
                            />
                        </Route>

                        {/* Password reset & invite — accessible even when logged in */}
                        <Route
                            path="/forgot-password"
                            element={<ForgotPasswordPage />}
                        />
                        <Route
                            path="/reset-password"
                            element={<ResetPasswordPage />}
                        />
                        <Route
                            path="/accept-invite"
                            element={<AcceptInvitePage />}
                        />

                        {/* Setup password — accessible to both guests and logged-in legacy users */}
                        <Route
                            path="/setup-password"
                            element={<SetupPasswordPage />}
                        />

                        {/* Shopify direct install — public route with internal auth redirect */}
                        <Route
                            path="/complete-account"
                            element={<CompleteAccountPage />}
                        />

                        {/* ═══ Auth Callback Routes (no guard) ═══ */}
                        {/* ─── [OUT OF SCOPE: Magic Link Auth] ─── */}
                        {/* <Route
                            path="/auth/magic-link/verify"
                            element={<MagicLinkVerifyPage />}
                        /> */}
                        {/* ─── [/OUT OF SCOPE] ─── */}
                        {/* ─── [RELEASE 3: Google OAuth] ─── */}
                        {/* <Route
                            path="/auth/google/callback"
                            element={<GoogleOAuthCallbackPage />}
                        /> */}
                        {/* ─── [/RELEASE 3] ─── */}
                        <Route
                            path="/verify-email"
                            element={<VerifyEmailPage />}
                        />

                        {/* ═══ Legacy Shopify Login (backward compat) ═══ */}
                        <Route
                            path="/shopify/login"
                            element={<LegacyLogin />}
                        />

                        {/* ═══ Onboarding Routes ═══ */}
                        <Route element={<OnboardingLayout />}>
                            <Route
                                path="/onboarding"
                                element={
                                    isAuthenticated ? (
                                        <Onboarding />
                                    ) : (
                                        <Navigate to="/login" replace />
                                    )
                                }
                            />
                            <Route
                                path="/book-a-call"
                                element={<BookACall />}
                            />
                            <Route
                                path="/billing-plans"
                                element={
                                    <PermissionGuard permission={PERMISSIONS.BILLING_WRITE}>
                                        <Billing />
                                    </PermissionGuard>
                                }
                            />
                        </Route>

                        {/* ═══ Private Routes (AuthGuard) ═══ */}
                        <Route element={<AuthGuard />}>
                            {/* ─── [RELEASE 3: Super Admin Panel] ─── */}
                            <Route element={<SuperAdminGuard />}>
                                <Route element={<AdminLayout />}>
                                    <Route path="/admin" element={<Navigate to="/admin/merchants" replace />} />
                                    <Route path="/admin/merchants" element={<MerchantsPage />} />
                                    <Route path="/admin/users" element={<AdminUsersPage />} />
                                    <Route path="/admin/devices" element={<DeviceManagementPage />} />
                                    <Route path="/admin/plans" element={<PlansPage />} />
                                    <Route path="/admin/billing" element={<BillingDealsPage />} />
                                    <Route path="/admin/audit-log" element={<AuditLogPage />} />
                                </Route>
                            </Route>
                            {/* ─── [/RELEASE 3] ─── */}

                            {/* Agentic Mode — full-viewport route, outside Layout */}
                            <Route
                                path="/agent"
                                element={<AgenticRoute />}
                            />

                            {/* Survey builder routes (outside Layout) */}
                            <Route
                                path="/builder/:surveyId"
                                element={
                                    <PermissionGuard permission={[PERMISSIONS.SURVEYS_WRITE, PERMISSIONS.TEMPLATES_WRITE]}>
                                        <SurveyBuilder />
                                    </PermissionGuard>
                                }
                            />
                            <Route
                                path="/template/:templateId"
                                element={
                                    <PermissionGuard permission={PERMISSIONS.TEMPLATES_READ}>
                                        <TemplateEditor isCreatingSurvey={false} />
                                    </PermissionGuard>
                                }
                            />
                            {/* Survey creation routes (Create/Template have their own store checks) */}
                            <Route
                                path="/surveys/scratch/:channel"
                                element={
                                    <PermissionGuard permission={PERMISSIONS.SURVEYS_WRITE}>
                                        <Create />
                                    </PermissionGuard>
                                }
                            />
                            <Route
                                path="/surveys/template/:templateId"
                                element={
                                    <PermissionGuard permission={PERMISSIONS.SURVEYS_WRITE}>
                                        <Template />
                                    </PermissionGuard>
                                }
                            />
                            <Route
                                path="/template/scratch/:channel"
                                element={
                                    <PermissionGuard permission={PERMISSIONS.TEMPLATES_WRITE}>
                                        <CreateTemplate />
                                    </PermissionGuard>
                                }
                            />

                            {/* Main layout routes */}
                            <Route element={<Layout />}>
                                <Route
                                    path="/"
                                    element={
                                        isSuperAdmin && !isImpersonating ? (
                                            <Navigate to="/admin/merchants" replace />
                                        ) : (
                                            <PermissionGuard permission={PERMISSIONS.SURVEYS_READ}>
                                                <Surveys />
                                            </PermissionGuard>
                                        )
                                    }
                                />
                                <Route
                                    path="/surveys/templates"
                                    element={
                                        <PermissionGuard permission={PERMISSIONS.TEMPLATES_READ}>
                                            <SurveyTemplates />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="/settings"
                                    element={<Navigate to="/settings/profile" replace />}
                                />
                                <Route
                                    path="/settings/integrations"
                                    element={
                                        <PermissionGuard permission={PERMISSIONS.INTEGRATIONS_READ}>
                                            <IntegrationsPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="/settings/team"
                                    element={
                                        <PermissionGuard
                                            permission={PERMISSIONS.TEAM_MANAGE}
                                            featureKey="team_module"
                                        >
                                            <TeamPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="/settings/billing"
                                    element={
                                        <PermissionGuard permission={PERMISSIONS.BILLING_READ}>
                                            <BillingPage />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="/settings/mcp"
                                    element={<McpPage />}
                                />
                                <Route
                                    path="/settings/profile"
                                    element={<ProfilePage />}
                                />
                                <Route
                                    path="/help"
                                    element={<HelpCenter />}
                                />
                                <Route
                                    path="/settings/whatsapp"
                                    element={<WhatsAppSettings />}
                                />
                                <Route
                                    path="/settings/whatsapp/conversations"
                                    element={<ConversationMonitor />}
                                />
                                <Route path="/help" element={<HelpCenter />} />
                                <Route
                                    path="/surveys/analytics/:surveyId"
                                    element={
                                        <PermissionGuard permission={PERMISSIONS.ANALYTICS_READ}>
                                            <Analytics />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="/analytics/nps-by-customer"
                                    element={
                                        <PermissionGuard permission={PERMISSIONS.ANALYTICS_READ}>
                                            <NpsByCustomer />
                                        </PermissionGuard>
                                    }
                                />
                                <Route
                                    path="/surveys/templates/mnps"
                                    element={<MeasureNetPromoterScore />}
                                />
                                <Route
                                    path="/surveys/ai-generate"
                                    element={
                                        <StoreRequiredGuard>
                                            <PermissionGuard permission={PERMISSIONS.SURVEYS_WRITE}>
                                                <AISurveyGenerator />
                                            </PermissionGuard>
                                        </StoreRequiredGuard>
                                    }
                                />
                            </Route>
                        </Route>

                        {/* ═══ Catch-all ═══ */}
                        <Route
                            path="*"
                            element={<Navigate to="/" replace />}
                        />
                    </Routes>
                </Suspense>
                <PlanUpgradeSuccessModal
                    open={showUpgradeSuccess}
                    onOpenChange={setShowUpgradeSuccess}
                />
                <CreateSurveyModal />
            </MantineProvider>
        </BillingProvider>
    )
}

// ─── Root App Component ───────────────────────────────────────────
function App() {
    return <AppContent />
}

export default App
